import React, { useEffect, createElement } from 'react';
interface SEOProps {
  title?: string;
  description?: string;
  canonicalUrl?: string;
}
export function SEO({
  title = '3 DOTS Travel | Bespoke Luxury Travel Design Studio',
  description = '3 DOTS Travel is a boutique travel design studio crafting highly personalized journeys across Europe, Middle East, Asia, and Africa. Bespoke itineraries, insider access, and white-glove service for discerning travelers.',
  canonicalUrl = 'https://www.3dotstravel.com'
}: SEOProps) {
  useEffect(() => {
    // Document title
    document.title = title;
    // Meta tags
    const metaTags: Record<string, string> = {
      description,
      'og:title': title,
      'og:description': description,
      'og:type': 'website',
      'og:url': canonicalUrl,
      'og:site_name': '3 DOTS Travel',
      'og:locale': 'en_US',
      'twitter:card': 'summary_large_image',
      'twitter:title': title,
      'twitter:description': description,
      robots: 'index, follow',
      'theme-color': '#1F2D5A',
      viewport: 'width=device-width, initial-scale=1'
    };
    Object.entries(metaTags).forEach(([name, content]) => {
      const isOg = name.startsWith('og:');
      const isTwitter = name.startsWith('twitter:');
      const attr = isOg || isTwitter ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attr, name);
        document.head.appendChild(meta);
      }
      meta.content = content;
    });
    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = canonicalUrl;
    // Language
    document.documentElement.lang = 'en';
    // JSON-LD Schema - Organization
    const organizationSchema = {
      '@context': 'https://schema.org',
      '@type': 'TravelAgency',
      name: '3 DOTS Travel',
      alternateName: '3DOTS Travel',
      url: canonicalUrl,
      logo: 'https://cdn.magicpatterns.com/uploads/uCxAG9G8XRJkgLABzP5wZi/3dots-logo-white-sanitized.svg',
      description: 'A boutique travel design studio creating highly personalized journeys across the world. Bespoke itineraries with insider access for discerning travelers.',
      priceRange: '$$$',
      areaServed: [{
        '@type': 'Continent',
        name: 'Europe'
      }, {
        '@type': 'Continent',
        name: 'Asia'
      }, {
        '@type': 'Continent',
        name: 'Africa'
      }, {
        '@type': 'Place',
        name: 'Middle East'
      }, {
        '@type': 'Place',
        name: 'Caucasus'
      }],
      serviceType: ['Bespoke Travel Planning', 'Luxury Travel Design', 'Private Tours', 'Cultural Immersion Experiences', 'Adventure Travel to Unconventional Destinations'],
      knowsAbout: ['Luxury travel', 'Bespoke itinerary design', 'Cultural immersion travel', 'Middle East travel', 'Caucasus travel', 'Off-the-beaten-path destinations', 'Private guided tours', 'Travel to unconventional destinations'],
      slogan: 'Travel, Curated Without Compromise',
      foundingDate: '2020',
      sameAs: []
    };
    // JSON-LD Schema - WebSite (for AI search)
    const websiteSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: '3 DOTS Travel',
      url: canonicalUrl,
      description,
      publisher: {
        '@type': 'Organization',
        name: '3 DOTS Travel'
      }
    };
    // JSON-LD Schema - Service offerings
    const serviceSchema = {
      '@context': 'https://schema.org',
      '@type': 'Service',
      serviceType: 'Bespoke Travel Design',
      provider: {
        '@type': 'TravelAgency',
        name: '3 DOTS Travel'
      },
      description: 'Highly personalized travel journeys designed with intelligence, intuition, and insider access. Each itinerary is created individually — never templated, never repeated.',
      areaServed: ['Europe', 'Middle East', 'Caucasus', 'Asia', 'Africa', 'Iraq', 'Iran', 'Afghanistan', 'Yemen', 'Somalia', 'North Korea'],
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Travel Experiences',
        itemListElement: [{
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Bespoke European Journeys',
            description: 'Curated travel through Slovenia, Italy, the Balkans, and the Mediterranean — beyond the guidebook.'
          }
        }, {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Middle East & Caucasus Exploration',
            description: 'Journeys through Jordan, Georgia, Armenia, and Iran — ancient civilizations and profound hospitality.'
          }
        }, {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Asian Cultural Immersion',
            description: 'Travel through Japan, Central Asia, and Southeast Asia — spiritual depth and culinary mastery.'
          }
        }, {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'African Wilderness Experiences',
            description: 'Select African destinations designed with care — vast wilderness and transformative encounters.'
          }
        }, {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Beyond the Comfort Zone',
            description: 'Carefully managed, ethically guided experiences in destinations often misunderstood or rarely explored, including Iraq, Iran, Afghanistan, Yemen, Somalia, and North Korea.'
          }
        }]
      }
    };
    // FAQ Schema for AI search
    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [{
        '@type': 'Question',
        name: 'What is 3 DOTS Travel?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '3 DOTS Travel is a privately owned boutique travel design studio creating highly personalized journeys across the world. We work with a limited number of clients each year, focusing on depth, detail, and exceptional execution.'
        }
      }, {
        '@type': 'Question',
        name: 'What destinations does 3 DOTS Travel cover?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '3 DOTS Travel designs bespoke journeys across Europe (Slovenia, Italy, Balkans, Mediterranean), Middle East & Caucasus (Jordan, Georgia, Armenia, Iran), Asia (Japan, Central Asia, Southeast Asia), and Africa. We also offer carefully managed trips to unconventional destinations like Iraq, Iran, Afghanistan, Yemen, Somalia, and North Korea.'
        }
      }, {
        '@type': 'Question',
        name: 'How does the travel planning process work?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Our process has four steps: (1) Consultation — a personal conversation to understand your vision, (2) Design — a bespoke itinerary crafted with intention, (3) Refinement — every detail refined until effortless, (4) Execution — full support before, during, and after your journey.'
        }
      }, {
        '@type': 'Question',
        name: 'Who does 3 DOTS Travel work with?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'We work with seasoned global travelers, private families and couples, creative leaders and entrepreneurs, and travel advisors and concierges. Privacy, trust, and long-term relationships define our work.'
        }
      }]
    };
    // Inject all schemas
    const schemas = [organizationSchema, websiteSchema, serviceSchema, faqSchema];
    const existingScripts = document.querySelectorAll('script[data-seo-schema]');
    existingScripts.forEach(el => el.remove());
    schemas.forEach((schema, index) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo-schema', `schema-${index}`);
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });
    // Cleanup
    return () => {
      const scripts = document.querySelectorAll('script[data-seo-schema]');
      scripts.forEach(el => el.remove());
    };
  }, [title, description, canonicalUrl]);
  return null;
}